"use client";
import React, { FC, Fragment, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslations } from "next-intl";

import { IFilterOption } from "@/types/parcel-filter.types";

import { ShowWhen } from "@/components/common";
import { ArrowDownIcon } from "@/components/assets/icons";
import FilterResult from "./filter-result";
import { Category } from "@/components/dashboard/parcels/get-parcels-table/components";

import { FilterType, ParcelCategoryUZ } from "@/enums";

import { useOutsideClickDetector } from "@/hooks/useOutsideClickDetector";
import { IFilterItem } from "./arrived-parcels-filter-box";

type Props = {
  item: IFilterItem;
  handleFilterChange: (type: FilterType, value: string | number) => void;
  handleSelectAll: (type: FilterType, options: IFilterOption[]) => void;
  withBorder?: boolean;
};

const Filter: FC<Props> = ({
  item,
  handleFilterChange,
  handleSelectAll,
  withBorder = true,
}) => {
  const { label, icon, translation, value, type, options } = item;
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef(null);

  const t = useTranslations();

  useOutsideClickDetector(ref, () => {
    setIsOpened(false);
  });

  const Icon = icon;

  return (
    <div className="flex cursor-pointer items-center gap-2">
      <div
        ref={ref}
        className="relative flex h-14 w-full min-w-[13.62rem] rounded-[33.50px] bg-[#f9f9f9]"
      >
        <div
          onClick={() => {
            setIsOpened(!isOpened);
          }}
          className="z-[99] flex w-full items-center justify-between p-[5px] pr-[20px]"
        >
          <div className="flex items-center gap-[8px]">
            {icon ? (
              <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white p-3">
                <Icon />
              </div>
            ) : null}
            <p
              className={classNames("text-base font-normal text-[#a8a8a8]", {
                "pl-1": value.length !== 0,
              })}
            >
              {t(label)}
            </p>
            <ShowWhen condition={value.length > 0}>
              {options
                .filter((option) => value?.includes(option.value))
                .slice(0, 2)
                .map((e, index) => {
                  const Icon = e.icon;

                  return (
                    <Fragment key={`${e.label}-${index}`}>
                      {type === FilterType.CATEGORY ? (
                        <Category
                          wrapperClassName="w-8 h-8"
                          category={e.value as ParcelCategoryUZ}
                        />
                      ) : e.icon ? (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f9f9f9]">
                          <Icon />
                        </div>
                      ) : null}
                    </Fragment>
                  );
                })}
            </ShowWhen>
          </div>
          <div className="flex cursor-pointer items-center">
            {value.length === 0 && (
              <p className="ml-2 text-base font-normal text-black">
                {t("any")}
              </p>
            )}
            <ArrowDownIcon />
          </div>
        </div>
        <ShowWhen condition={isOpened}>
          <FilterResult
            options={options}
            value={value}
            type={type}
            handleFilterChange={handleFilterChange}
            handleSelectAll={handleSelectAll}
            translation={translation}
          />
        </ShowWhen>
      </div>
      <ShowWhen condition={withBorder}>
        <div className="w-[25px] rotate-90 border border-[#f1f1f1]" />
      </ShowWhen>
    </div>
  );
};

export { Filter };
